import moment from 'moment';

import { getQuillPlainText } from '../../../../../components/baseV2';
import { API_DATE_FORMAT } from '../../../../../constants';
import { TREATMENT_SERVICE_FREQUENCIES } from '../../../../../store/treatmentPlanV3';
import {
  TREATMENT_PROVISIONING_REQUIRED_BAYS,
  TREATMENT_SERVICE_ROWS,
  TREATMENT_UNITS_PER_HOUR,
} from './constants';
import { TREATMENT_FORM_SCHEMA } from './validationSchema';

import type {
  ITreatmentPlanV3TreatmentPage,
  TreatmentPlanV3TreatmentPagePayload,
  TreatmentServiceFrequency,
  TreatmentServicePerFieldName,
} from '../../../../../store/treatmentPlanV3';
import type { Bays } from '../../../../../utils/types';
import type { TreatmentFieldName, TreatmentFormValues } from './types';

export function mapTreatmentPageToSavedValues(
  page: ITreatmentPlanV3TreatmentPage,
): TreatmentFormValues {
  const services = page.services ?? {};
  return {
    recommendations_explanation: page.recommendations_explanation ?? '',
    proposed_start_date: page.proposed_start_date
      ? moment(page.proposed_start_date, API_DATE_FORMAT, true)
      : null,
    duration_in_months: page.duration_in_months?.toString() ?? '',
    assessment_97151_hours: services.assessment_97151?.hours?.toString() ?? '',
    assessment_97151_per: services.assessment_97151?.per ?? '',
    assessment_97152_hours: services.assessment_97152?.hours?.toString() ?? '',
    assessment_97152_per: services.assessment_97152?.per ?? '',
    direct_therapy_97153_hours: services.direct_therapy_97153?.hours?.toString() ?? '',
    direct_therapy_97153_per: services.direct_therapy_97153?.per ?? '',
    protocol_modification_97155_hours:
      services.protocol_modification_97155?.hours?.toString() ?? '',
    protocol_modification_97155_per: services.protocol_modification_97155?.per ?? '',
    parent_training_97156_hours: services.parent_training_97156?.hours?.toString() ?? '',
    parent_training_97156_per: services.parent_training_97156?.per ?? '',
    group_parent_training_97157_hours:
      services.group_parent_training_97157?.hours?.toString() ?? '',
    group_parent_training_97157_per: services.group_parent_training_97157?.per ?? '',
    care_coordination_99366_hours: services.care_coordination_99366?.hours?.toString() ?? '',
    care_coordination_99366_per: services.care_coordination_99366?.per ?? '',
  };
}

export function mapTreatmentPageToValues(page: ITreatmentPlanV3TreatmentPage): TreatmentFormValues {
  const values = mapTreatmentPageToSavedValues(page);
  TREATMENT_SERVICE_ROWS.forEach(({ hoursName, perName, defaultPer }) => {
    if (values[perName] === '' && Number(values[hoursName] || 0) === 0) {
      values[perName] = defaultPer;
    }
  });
  return values;
}

export function isTherapyProvisioningDescriptionRequired(bay: Bays | undefined) {
  return TREATMENT_PROVISIONING_REQUIRED_BAYS.some((requiredBay) => requiredBay === bay);
}

export function hasTreatmentFormErrors(page: ITreatmentPlanV3TreatmentPage, bay: Bays | undefined) {
  if (isTherapyProvisioningDescriptionRequired(bay) && !page.therapy_provisioning_description) {
    return true;
  }
  return !TREATMENT_FORM_SCHEMA.isValidSync(mapTreatmentPageToValues(page));
}

function isTreatmentServicePerFieldName(
  name: TreatmentFieldName,
): name is TreatmentServicePerFieldName {
  return name.endsWith('_per');
}

function isTreatmentServiceFrequency(value: string): value is TreatmentServiceFrequency {
  return TREATMENT_SERVICE_FREQUENCIES.some((frequency) => frequency === value);
}

export function toTreatmentFieldPayload(
  name: TreatmentFieldName,
  values: TreatmentFormValues,
): TreatmentPlanV3TreatmentPagePayload {
  if (name === 'proposed_start_date') {
    return { proposed_start_date: values.proposed_start_date?.format(API_DATE_FORMAT) ?? null };
  }
  if (name === 'recommendations_explanation') {
    return {
      recommendations_explanation: getQuillPlainText(values.recommendations_explanation)
        ? values.recommendations_explanation
        : null,
    };
  }
  if (isTreatmentServicePerFieldName(name)) {
    const per = values[name];
    return { [name]: isTreatmentServiceFrequency(per) ? per : null };
  }
  return { [name]: values[name] === '' ? null : Number(values[name]) };
}

export function getTreatmentServiceUnits(hours: string) {
  return /^\d+$/.test(hours) ? String(Number(hours) * TREATMENT_UNITS_PER_HOUR) : '0';
}
